// Airport → IANA timezone map. Airlines publish schedule times in the
// airport's OWN local time, so every station needs its own TZ for both
// storage (local→UTC on import) and display (UTC→local on the QR portal
// and admin views). Without this, a Pacific flight typed as "04:30" would
// be stored as if it were 4:30 AM Eastern (misaligned by 3 hours during
// summer) and Pacific-based managers would see wrong times.
//
// Any code not in this map falls back to America/New_York (safe for the
// current ET-dominated fleet; add entries as new stations come online).
const TZ_ET = "America/New_York";
const TZ_CT = "America/Chicago";
const TZ_MT = "America/Denver";
const TZ_PT = "America/Los_Angeles";
const TZ_AZ = "America/Phoenix";   // Arizona — MST year-round, no DST
const TZ_AT = "America/Puerto_Rico"; // AST year-round, no DST

export const AIRPORT_TZ = {
  // ---- Pacific ----
  BUR: TZ_PT, LAS: TZ_PT, LAX: TZ_PT, LGB: TZ_PT, LSV: TZ_PT,
  OAK: TZ_PT, ONT: TZ_PT, PDX: TZ_PT, RNO: TZ_PT, SAN: TZ_PT,
  SEA: TZ_PT, SFO: TZ_PT, SJC: TZ_PT, SMF: TZ_PT, SNA: TZ_PT,
  STS: TZ_PT,

  // ---- Arizona (year-round MST, no DST) ----
  PHX: TZ_AZ, MZJ: TZ_AZ,

  // ---- Mountain (with DST) ----
  ABQ: TZ_MT, COS: TZ_MT, DEN: TZ_MT, ELP: TZ_MT,

  // ---- Central ----
  AEX: TZ_CT, AFW: TZ_CT, AUS: TZ_CT, BNA: TZ_CT, DAL: TZ_CT,
  DFW: TZ_CT, GGG: TZ_CT, HOU: TZ_CT, IAH: TZ_CT, MCI: TZ_CT,
  MDW: TZ_CT, MEM: TZ_CT, MKE: TZ_CT, MLU: TZ_CT, MSP: TZ_CT,
  MSY: TZ_CT, OKC: TZ_CT, ORD: TZ_CT, SDF: TZ_ET, // Louisville is Eastern
  STL: TZ_CT, TUL: TZ_CT,
  VPS: TZ_CT, // Florida panhandle (Eglin AFB) — Central

  // ---- Atlantic (Puerto Rico) ----
  SJU: TZ_AT,

  // ---- Eastern (default) ----
  ATL: TZ_ET, BOS: TZ_ET, BUF: TZ_ET, BWI: TZ_ET, CAK: TZ_ET,
  CLT: TZ_ET, CMH: TZ_ET, CVG: TZ_ET, DAY: TZ_ET, DCA: TZ_ET,
  DTW: TZ_ET, DUJ: TZ_ET, EWR: TZ_ET, FLL: TZ_ET, FWA: TZ_ET,
  IAD: TZ_ET, IND: TZ_ET, JAX: TZ_ET, JFK: TZ_ET, LEX: TZ_ET,
  LGA: TZ_ET, MCO: TZ_ET, MIA: TZ_ET, MLB: TZ_ET, OPF: TZ_ET,
  PBI: TZ_ET, PHL: TZ_ET, PIT: TZ_ET, RDU: TZ_ET, RIV: TZ_ET,
  RSW: TZ_ET, SAV: TZ_ET, SCE: TZ_ET, TPA: TZ_ET, TTN: TZ_ET,
  VRB: TZ_ET,
};

export function tzForAirport(code) {
  return AIRPORT_TZ[(code || "").toUpperCase()] || TZ_ET;
}

// Standard IANA-offset getter for the four TZs above on a given date.
// Avoids pulling in the full dayjs-timezone plugin — we only need a
// handful of zones and their fixed DST rules.
export function offsetHoursFor(tzName, y, m, d) {
  if (tzName === TZ_AZ) return -7;
  if (tzName === TZ_AT) return -4;

  // US DST: 2nd Sunday of March 02:00 → 1st Sunday of November 02:00
  const marchFirstDow = new Date(Date.UTC(y, 2, 1)).getUTCDay();
  const secondSunOfMarch = 1 + ((7 - marchFirstDow) % 7) + 7;
  const novFirstDow = new Date(Date.UTC(y, 10, 1)).getUTCDay();
  const firstSunOfNov = 1 + ((7 - novFirstDow) % 7);
  const afterStart = m > 2 || (m === 2 && d >= secondSunOfMarch);
  const beforeEnd = m < 10 || (m === 10 && d < firstSunOfNov);
  const isDst = afterStart && beforeEnd;

  if (tzName === TZ_ET) return isDst ? -4 : -5;
  if (tzName === TZ_CT) return isDst ? -5 : -6;
  if (tzName === TZ_MT) return isDst ? -6 : -7;
  if (tzName === TZ_PT) return isDst ? -7 : -8;
  return isDst ? -4 : -5; // fallback ET
}

// Interpret naive Y/M/D H:M as local time in the given airport TZ, return
// the equivalent UTC Date.
export function airportLocalToUtc(airportCode, y, m, d, hh, mi) {
  const tz = tzForAirport(airportCode);
  const off = offsetHoursFor(tz, y, m, d);
  return new Date(Date.UTC(y, m, d, hh - off, mi));
}

// Given a UTC Date/ISO/dayjs-parseable value and an airport code, return
// {hour, minute} in the airport's local timezone. Used for computing the
// hour-of-day window match on CrewTimeAdjustments regardless of who's
// looking at the screen.
export function localHourMinute(utc, airportCode) {
  const d = utc instanceof Date ? utc : new Date(utc);
  const tz = tzForAirport(airportCode);
  const off = offsetHoursFor(
    tz,
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate()
  );
  const localMs = d.getTime() + off * 3600 * 1000;
  const local = new Date(localMs);
  return { hour: local.getUTCHours(), minute: local.getUTCMinutes() };
}

// Format a UTC value as "h:mm A" in the airport's local timezone.
// Independent of the viewer's browser timezone.
export function formatInAirportTz(utc, airportCode) {
  const d = utc instanceof Date ? utc : new Date(utc);
  if (isNaN(d.getTime())) return "";
  const { hour, minute } = localHourMinute(d, airportCode);
  const h12 = ((hour + 11) % 12) + 1;
  const suffix = hour < 12 ? "AM" : "PM";
  return `${h12}:${String(minute).padStart(2, "0")} ${suffix}`;
}
